import React, { useState, useRef, useEffect, useMemo } from 'react';

/**
 * DatePicker - Shared date selector for all views.
 * Always shows the formatted date. Clicking the date opens a calendar grid.
 * "Today" button permanently on the left. Back/forward arrows for quick navigation.
 */
export default function DatePicker({ selectedDate, onDateChange, isKiosk = false }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [viewMonth, setViewMonth] = useState(null); // YYYY-MM for the calendar grid
  const calRef = useRef(null);

  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;

  const dateObj = new Date(selectedDate + 'T12:00:00');
  const label = dateObj.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  // Close calendar when clicking outside
  useEffect(() => {
    if (!showCalendar) return;
    const handleClick = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showCalendar]);

  // When opening calendar, set viewMonth to the selected date's month
  const openCalendar = () => {
    setViewMonth(selectedDate.slice(0, 7));
    setShowCalendar(true);
  };

  const goBack = () => {
    const prev = new Date(dateObj);
    prev.setDate(prev.getDate() - 1);
    const prevStr = prev.toISOString().split('T')[0];
    if (prevStr >= today) {
      onDateChange(prevStr);
    }
  };

  const goForward = () => {
    const next = new Date(dateObj);
    next.setDate(next.getDate() + 1);
    onDateChange(next.toISOString().split('T')[0]);
  };

  const goToday = () => {
    onDateChange(today);
    setShowCalendar(false);
  };

  const selectDate = (dateStr) => {
    if (dateStr >= today) {
      onDateChange(dateStr);
      setShowCalendar(false);
    }
  };

  return (
    <div className={`date-picker ${isKiosk ? 'date-picker--kiosk' : ''}`} ref={calRef}>
      <button
        className={`date-picker-btn date-picker-today ${isToday ? 'date-picker-today--active' : ''}`}
        onClick={goToday}
        disabled={isToday}
      >
        Today
      </button>

      <button
        className="date-picker-btn date-picker-arrow"
        onClick={goBack}
        disabled={isToday}
        aria-label="Previous day"
      >
        &#8249;
      </button>

      <div className="date-picker-display" onClick={openCalendar}>
        <span className="date-picker-label">{label}</span>
      </div>

      <button
        className="date-picker-btn date-picker-arrow"
        onClick={goForward}
        aria-label="Next day"
      >
        &#8250;
      </button>

      {showCalendar && (
        <CalendarGrid
          viewMonth={viewMonth}
          onChangeMonth={setViewMonth}
          selectedDate={selectedDate}
          today={today}
          onSelectDate={selectDate}
          isKiosk={isKiosk}
        />
      )}
    </div>
  );
}

/**
 * CalendarGrid - Monthly calendar popup.
 */
function CalendarGrid({ viewMonth, onChangeMonth, selectedDate, today, onSelectDate, isKiosk }) {
  const [year, month] = viewMonth.split('-').map(Number);

  const monthLabel = new Date(year, month - 1, 15).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const todayMonth = today.slice(0, 7);

  // Build the calendar grid
  const days = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDow = firstDay.getDay(); // 0=Sun
    const totalDays = lastDay.getDate();

    const cells = [];
    // Empty cells before first day
    for (let i = 0; i < startDow; i++) {
      cells.push(null);
    }
    // Day cells
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push(dateStr);
    }
    return cells;
  }, [year, month]);

  const prevMonth = () => {
    const prev = new Date(year, month - 2, 1);
    const prevStr = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
    if (prevStr >= todayMonth) {
      onChangeMonth(prevStr);
    }
  };

  const nextMonth = () => {
    const next = new Date(year, month, 1);
    onChangeMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
  };

  const canGoPrev = viewMonth > todayMonth;

  return (
    <div className={`cal-popup ${isKiosk ? 'cal-popup--kiosk' : ''}`}>
      <div className="cal-header">
        <button
          className="cal-nav-btn"
          onClick={prevMonth}
          disabled={!canGoPrev}
        >
          &#8249;
        </button>
        <span className="cal-month-label">{monthLabel}</span>
        <button className="cal-nav-btn" onClick={nextMonth}>
          &#8250;
        </button>
      </div>

      <div className="cal-dow-row">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <span key={d} className="cal-dow">{d}</span>
        ))}
      </div>

      <div className="cal-grid">
        {days.map((dateStr, i) => {
          if (!dateStr) {
            return <span key={`empty-${i}`} className="cal-cell cal-cell--empty" />;
          }
          const isPast = dateStr < today;
          const isSelected = dateStr === selectedDate;
          const isToday = dateStr === today;
          const dayNum = parseInt(dateStr.split('-')[2], 10);

          return (
            <button
              key={dateStr}
              className={[
                'cal-cell',
                isPast ? 'cal-cell--past' : '',
                isSelected ? 'cal-cell--selected' : '',
                isToday ? 'cal-cell--today' : '',
              ].join(' ')}
              onClick={() => !isPast && onSelectDate(dateStr)}
              disabled={isPast}
            >
              {dayNum}
            </button>
          );
        })}
      </div>
    </div>
  );
}
